import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ANALYTICS_CONSENT_EVENT,
  getAnalyticsConsent,
  setAnalyticsConsent,
  type AnalyticsConsent,
} from "../../lib/analyticsConsent";
import "./ConsentBar.css";
import { useT } from "../../i18n/i18nContext";

/**
 * The analytics consent bar — asked once, on any page, until the player chooses. "Only essential"
 * is exactly as easy as "Accept" (same size, same row): a choice, not a nag. "Cookie settings" in
 * the footer forgets the choice and brings the bar back. What each answer does is in
 * lib/analyticsConsent.ts; the Privacy page describes it.
 */
export function ConsentBar() {
  const t = useT();
  const [choice, setChoice] = useState<AnalyticsConsent | null>(() => getAnalyticsConsent());

  useEffect(() => {
    const onConsent = (event: Event) => setChoice((event as CustomEvent<AnalyticsConsent | null>).detail);
    window.addEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
    return () => window.removeEventListener(ANALYTICS_CONSENT_EVENT, onConsent);
  }, []);

  if (choice) return null;

  return (
    <div className="consent-bar" role="region" aria-label={t("navigation.consent.aria")}>
      <p className="consent-bar__text">
        {t("navigation.consent.text")}{" "}
        <Link to="/privacy" className="consent-bar__link">{t("navigation.consent.privacy")}</Link>
      </p>
      <div className="consent-bar__actions">
        <button type="button" className="btn btn--secondary btn--md" onClick={() => setAnalyticsConsent("denied")}>
          {t("navigation.consent.essential")}
        </button>
        <button type="button" className="btn btn--primary btn--md" onClick={() => setAnalyticsConsent("granted")}>
          {t("navigation.consent.accept")}
        </button>
      </div>
    </div>
  );
}
